import axios from 'axios';
import { getIdToken, getCurrentUser, wasAuthenticated } from './cognito';

// Custom error class for session expiration
export class AuthSessionExpiredError extends Error {
  constructor(message) {
    super(message);
    this.name = 'AuthSessionExpiredError';
  }
}

const api = axios.create({
  baseURL: `${import.meta.env.VITE_API_BASE_URL}/api/v1`,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor - attach token
api.interceptors.request.use(
  async (config) => {
    // Skip auth for public endpoints
    const publicPaths = ['/auth/register', '/health'];
    if (publicPaths.some(path => config.url?.includes(path))) {
      return config;
    }

    // Check if user is authenticated before making request
    const user = getCurrentUser();
    const hadPreviousSession = wasAuthenticated();

    if (!user) {
      // No user session
      if (hadPreviousSession) {
        // User was authenticated but now getCurrentUser() returns null
        // This could be HMR/module reload - try to recover
        console.log('[API] Session marker exists but no user - likely HMR, attempting recovery');
        // Don't throw immediately, let the token retrieval attempt handle it
      } else {
        // Never authenticated in this session - not a session expiration
        console.log('[API] No authenticated user, skipping request');
        const error = new Error('Not authenticated');
        error.name = 'NotAuthenticatedError';
        throw error;
      }
    }

    try {
      const token = await getIdToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    } catch (error) {
      console.error('[API] Token retrieval failed:', error);

      // Only emit session_expired if there was a previous authenticated session
      // This prevents false positives during HMR/module reload transient states
      if (hadPreviousSession) {
        sessionStorage.setItem('auth_redirect_from', window.location.pathname);
        window.dispatchEvent(new CustomEvent('auth:session_expired', {
          detail: { error: error.message }
        }));
        throw new AuthSessionExpiredError('Your session has expired. Please log in again.');
      } else {
        // No previous session, just throw a regular auth error
        const authError = new Error('Not authenticated');
        authError.name = 'NotAuthenticatedError';
        throw authError;
      }
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor - handle errors
api.interceptors.response.use(
  (response) => {
    // Check for API-level errors in 2xx responses
    if (response.data?.error) {
      console.error('[API] Response contained error:', response.data.error);
      const error = new Error(response.data.error.message || 'API returned an error');
      error.response = response;
      error.apiError = response.data.error;
      return Promise.reject(error);
    }
    return response;
  },
  (error) => {
    // Don't log CanceledErrors - these are expected during cleanup
    if (error.name !== 'CanceledError') {
      console.error('[API] Request failed:', error);
    }

    // Check if already on auth page to prevent redirect loop
    const isAuthPage = ['/login', '/register', '/forgot-password'].some(
      path => window.location.pathname.startsWith(path)
    );
    const isAuthEndpoint = error.config?.url?.includes('/auth/');

    // Only emit session_expired if user was previously authenticated
    // This prevents false positives during transient states
    if (error.response?.status === 401 && !isAuthPage && !isAuthEndpoint && wasAuthenticated()) {
      // Emit session expired event
      window.dispatchEvent(new CustomEvent('auth:session_expired'));
    }

    return Promise.reject(error);
  }
);

export { api };
